/**
 * Generated by orval v7.17.0 🍺
 * Do not edit manually.
 * RIA Multi-Agent System
 * Remediation Intelligence Agent for audits and compliance automation
 * OpenAPI spec version: 1.0.0
 */
/**
 * Registered agent identifiers.
 */
export type Agent = (typeof Agent)[keyof typeof Agent]

// eslint-disable-next-line @typescript-eslint/no-redeclare
export const Agent = {
  RiaAgent: 'ria_agent',
  PolicyAgent: 'policy_agent',
  HrAgent: 'hr_agent',
  EntraAgent: 'entra_agent',
  TopdeskAgent: 'topdesk_agent',
  PeriodicAgent: 'periodic_agent',
  NotificationAgent: 'notification_agent',
} as const

/**
 * Last run timestamp in ISO format
 */
export type AgentStatusLastRun = string | null

/**
 * Agent status information
 */
export interface AgentStatus {
  /** Agent name */
  name: Agent
  /** Agent operational status (up, down, running) */
  status: AgentStatusType
  /** Agent uptime in seconds */
  uptime: number
  /** Agent start time in ISO format */
  start_time: string
  /** Unique agent identifier */
  id: string
  /** Agent description */
  description: string
  /** Agent type/category */
  type: string
  /** Last run timestamp in ISO format */
  last_run: AgentStatusLastRun
  /** Success rate percentage */
  success_rate: number
  /** Average processing time in milliseconds */
  processing_time: number
  /** Number of open/pending tasks */
  open_tasks: number
  /** Number of overdue tasks */
  overdue_tasks: number
  [key: string]: unknown
}

/**
 * Agent operational status.
 */
export type AgentStatusType =
  (typeof AgentStatusType)[keyof typeof AgentStatusType]

// eslint-disable-next-line @typescript-eslint/no-redeclare
export const AgentStatusType = {
  Up: 'up',
  Down: 'down',
  Running: 'running',
} as const

/**
 * Health status of each agent
 */
export type AgentsHealthCheckResponseAgents = { [key: string]: unknown }

/**
 * Health check response for all agents
 */
export interface AgentsHealthCheckResponse {
  /** Overall health status */
  status: string
  /** Health status of each agent */
  agents: AgentsHealthCheckResponseAgents
}

/**
 * Response for list of agents
 */
export interface AgentsListResponse {
  /** Total number of agents */
  total: number
  /** List of agent status details */
  agents: AgentStatus[]
}

/**
 * Role responsible for rule enforcement/execution.
 */
export type AssigneeRole = (typeof AssigneeRole)[keyof typeof AssigneeRole]

// eslint-disable-next-line @typescript-eslint/no-redeclare
export const AssigneeRole = {
  ITSecurityManager: 'IT Security Manager',
  HRCoordinator: 'HR Coordinator',
  HeadOfTech: 'Head of Tech',
  ICTManager: 'ICT Manager',
} as const

export type AuditEventEntry =
  | TaskExecutedAuditEvent
  | QueryExecutedAuditEvent
  | TaskCreatedAuditEvent
  | TaskApprovedAuditEvent
  | TaskRejectedAuditEvent
  | BatchTasksApprovedAuditEvent
  | BatchTasksRejectedAuditEvent
  | PolicyEvaluationRequestedAuditEvent
  | PolicyEvaluationCompletedAuditEvent
  | DataIngestedAuditEvent
  | PeriodicTaskCreatedAuditEvent
  | PeriodicTaskStartedAuditEvent
  | PeriodicTaskCompletedAuditEvent
  | PolicyRuleCreatedAuditEvent
  | PolicyRuleUpdatedAuditEvent
  | PolicyRuleDeletedAuditEvent
  | PolicyRulesSyncedAuditEvent
  | PolicyRulesSeededAuditEvent

/**
 * Response for audit log entries
 */
export interface AuditLogResponse {
  /** Total number of entries returned */
  total: number
  /** Audit log entries */
  entries: AuditEventEntry[]
}

/**
 * Request to approve multiple tasks (approved_by comes from X-User-Name header)
 */
export interface BatchApprovalRequest {
  task_ids: string[]
}

/**
 * Request to mark multiple notifications as read
 */
export interface BatchMarkReadRequest {
  /** List of notification IDs to mark as read */
  notification_ids: string[]
}

/**
 * Response for batch mark read operation
 */
export interface BatchMarkReadResponse {
  /** Overall operation result message */
  message: string
  /** Total number of notifications in the batch */
  total: number
  /** Number of successful operations */
  successful: number
  /** Number of failed operations */
  failed: number
  /** Individual results for each notification */
  results: BatchMarkReadResult[]
}

/**
 * Error message if failed
 */
export type BatchMarkReadResultError = string | null

/**
 * Result for a single notification in a batch operation
 */
export interface BatchMarkReadResult {
  /** Notification ID */
  notification_id: string
  /** Whether the operation succeeded */
  success: boolean
  /** Error message if failed */
  error?: BatchMarkReadResultError
}

export type BatchRejectionRequestReason = string | null

/**
 * Request to reject multiple tasks
 */
export interface BatchRejectionRequest {
  task_ids: string[]
  reason?: BatchRejectionRequestReason
}

/**
 * Response for batch task operations
 */
export interface BatchTaskOperationResponse {
  /** Overall operation result message */
  message: string
  /** Total number of tasks in the batch */
  total: number
  /** Number of successful operations */
  successful: number
  /** Number of failed operations */
  failed: number
  /** Individual results for each task */
  results: BatchTaskResult[]
}

/**
 * The task if successful
 */
export type BatchTaskResultTask = unknown | null

/**
 * Error message if failed
 */
export type BatchTaskResultError = string | null

/**
 * Result for a single task in a batch operation
 */
export interface BatchTaskResult {
  /** Task ID */
  task_id: string
  /** Whether the operation succeeded */
  success: boolean
  /** The task if successful */
  task?: BatchTaskResultTask
  /** Error message if failed */
  error?: BatchTaskResultError
}

/**
 * The task if successful
 */
export type BatchTaskResultPeriodicTaskTask = PeriodicTask | null

/**
 * Error message if failed
 */
export type BatchTaskResultPeriodicTaskError = string | null

export interface BatchTaskResultPeriodicTask {
  /** Task ID */
  task_id: string
  /** Whether the operation succeeded */
  success: boolean
  /** The task if successful */
  task?: BatchTaskResultPeriodicTaskTask
  /** Error message if failed */
  error?: BatchTaskResultPeriodicTaskError
}

/**
 * The task if successful
 */
export type BatchTaskResultRemediationTaskTask = RemediationTask | null

/**
 * Error message if failed
 */
export type BatchTaskResultRemediationTaskError = string | null

export interface BatchTaskResultRemediationTask {
  /** Task ID */
  task_id: string
  /** Whether the operation succeeded */
  success: boolean
  /** The task if successful */
  task?: BatchTaskResultRemediationTaskTask
  /** Error message if failed */
  error?: BatchTaskResultRemediationTaskError
}

/**
 * Audit event for batch task approval
 */
export interface BatchTasksApprovedAuditEvent {
  event_type: 'batch_tasks_approved_event'
  timestamp: string
  agent: string
  status: EventStatus
  approved_by: string
  total_tasks: number
  successful: number
}

export type BatchTasksRejectedAuditEventReason = string | null

/**
 * Audit event for batch task rejection
 */
export interface BatchTasksRejectedAuditEvent {
  event_type: 'batch_tasks_rejected_event'
  timestamp: string
  agent: string
  status: EventStatus
  rejected_by: string
  total_tasks: number
  successful: number
  reason: BatchTasksRejectedAuditEventReason
}

export interface BodyHrUploadSapFile {
  file: Blob
}

/**
 * Audit event for data ingestion
 */
export interface DataIngestedAuditEvent {
  event_type: 'data_ingested_event'
  timestamp: string
  agent: string
  status: EventStatus
  source_type: TaskSourceType
  record_count: number
}

/**
 * Status of audit events.
 */
export type EventStatus = (typeof EventStatus)[keyof typeof EventStatus]

// eslint-disable-next-line @typescript-eslint/no-redeclare
export const EventStatus = {
  Success: 'success',
  Failed: 'failed',
  Info: 'info',
} as const

/**
 * Rule ID (auto-generated if not provided)
 */
export type ExecutableRuleCreateRuleId = string | null

/**
 * Specific assignee email
 */
export type ExecutableRuleCreateAssigneeEmail = string | null

/**
 * Role to escalate to
 */
export type ExecutableRuleCreateEscalationRole = AssigneeRole | null

/**
 * Specific escalation email
 */
export type ExecutableRuleCreateEscalationEmail = string | null

/**
 * Excerpt from source policy document
 */
export type ExecutableRuleCreateSourceSnippet = string | null

/**
 * Request to create an executable rule
 */
export interface ExecutableRuleCreate {
  /** Rule ID (auto-generated if not provided) */
  rule_id?: ExecutableRuleCreateRuleId
  /** Rule category */
  category: string
  /** Rule title */
  title: string
  /** Rule description */
  description: string
  /** Conditions that must all be true */
  conditions: PolicyCondition[]
  /** Severity level */
  severity: PolicyRuleSeverity
  /** Tasks to execute when conditions match */
  tasks: PolicyTaskSpec[]
  /** Source policy document name */
  source_policy: string
  /** Role responsible for this rule */
  assignee_role: AssigneeRole
  /** Specific assignee email */
  assignee_email?: ExecutableRuleCreateAssigneeEmail
  /**
   * Hours until task escalates
   * @minimum 1
   */
  escalate_after?: number
  /** Role to escalate to */
  escalation_role?: ExecutableRuleCreateEscalationRole
  /** Specific escalation email */
  escalation_email?: ExecutableRuleCreateEscalationEmail
  /** Excerpt from source policy document */
  source_snippet?: ExecutableRuleCreateSourceSnippet
}

/**
 * Rule category
 */
export type ExecutableRuleUpdateCategory = string | null

/**
 * Rule title
 */
export type ExecutableRuleUpdateTitle = string | null

/**
 * Rule description
 */
export type ExecutableRuleUpdateDescription = string | null

/**
 * Conditions
 */
export type ExecutableRuleUpdateConditions = PolicyCondition[] | null

/**
 * Severity level
 */
export type ExecutableRuleUpdateSeverity = PolicyRuleSeverity | null

/**
 * Tasks to execute
 */
export type ExecutableRuleUpdateTasks = PolicyTaskSpec[] | null

/**
 * Source policy document name
 */
export type ExecutableRuleUpdateSourcePolicy = string | null

/**
 * Assignee role
 */
export type ExecutableRuleUpdateAssigneeRole = AssigneeRole | null

/**
 * Assignee email
 */
export type ExecutableRuleUpdateAssigneeEmail = string | null

/**
 * Hours until task escalates
 */
export type ExecutableRuleUpdateEscalateAfter = number | null

/**
 * Role to escalate to
 */
export type ExecutableRuleUpdateEscalationRole = AssigneeRole | null

/**
 * Specific escalation email
 */
export type ExecutableRuleUpdateEscalationEmail = string | null

/**
 * Excerpt from source policy document
 */
export type ExecutableRuleUpdateSourceSnippet = string | null

/**
 * Request to update an executable rule (all fields optional)
 */
export interface ExecutableRuleUpdate {
  /** Rule category */
  category?: ExecutableRuleUpdateCategory
  /** Rule title */
  title?: ExecutableRuleUpdateTitle
  /** Rule description */
  description?: ExecutableRuleUpdateDescription
  /** Conditions */
  conditions?: ExecutableRuleUpdateConditions
  /** Severity level */
  severity?: ExecutableRuleUpdateSeverity
  /** Tasks to execute */
  tasks?: ExecutableRuleUpdateTasks
  /** Source policy document name */
  source_policy?: ExecutableRuleUpdateSourcePolicy
  /** Assignee role */
  assignee_role?: ExecutableRuleUpdateAssigneeRole
  /** Assignee email */
  assignee_email?: ExecutableRuleUpdateAssigneeEmail
  /** Hours until task escalates */
  escalate_after?: ExecutableRuleUpdateEscalateAfter
  /** Role to escalate to */
  escalation_role?: ExecutableRuleUpdateEscalationRole
  /** Specific escalation email */
  escalation_email?: ExecutableRuleUpdateEscalationEmail
  /** Excerpt from source policy document */
  source_snippet?: ExecutableRuleUpdateSourceSnippet
}

/**
 * Response for list of executable rules
 */
export interface ExecutableRulesListResponse {
  /** Total number of rules */
  total: number
  /** List of rules */
  rules: PolicyExecutableRule[]
}

/**
 * Response for file upload
 */
export interface FileUploadResponse {
  /** Name of the uploaded file */
  filename: string
  /** Upload status */
  status: string
  /** Number of employees processed */
  total_employees: number
  /** Detailed result message */
  message: string
}

export interface HTTPValidationError {
  detail?: ValidationError[]
}

export type NotificationTaskId = string | null

export type NotificationTaskType = string | null

export type NotificationRecipientEmail = string | null

/**
 * Simple notification entity.
 */
export interface Notification {
  id: string
  type: NotificationType
  message: string
  task_id?: NotificationTaskId
  task_type?: NotificationTaskType
  recipient_email: NotificationRecipientEmail
  created_at: string
  read: boolean
}

/**
 * Type of notification.
 */
export type NotificationType =
  (typeof NotificationType)[keyof typeof NotificationType]

// eslint-disable-next-line @typescript-eslint/no-redeclare
export const NotificationType = {
  TaskCreated: 'task_created',
  TaskStarted: 'task_started',
  TaskCompleted: 'task_completed',
  TaskFailed: 'task_failed',
  TaskOverdue: 'task_overdue',
} as const

export interface NotificationsListResponse {
  total: number
  notifications: Notification[]
}

/**
 * Rule ID (auto-generated if not provided)
 */
export type PeriodicRuleCreateRuleId = string | null

/**
 * Specific assignee email
 */
export type PeriodicRuleCreateAssigneeEmail = string | null

/**
 * Role to escalate to
 */
export type PeriodicRuleCreateEscalationRole = AssigneeRole | null

/**
 * Specific escalation email
 */
export type PeriodicRuleCreateEscalationEmail = string | null

/**
 * Excerpt from source policy document
 */
export type PeriodicRuleCreateSourceSnippet = string | null

/**
 * Request to create a periodic rule
 */
export interface PeriodicRuleCreate {
  /** Rule ID (auto-generated if not provided) */
  rule_id?: PeriodicRuleCreateRuleId
  /** Rule category */
  category: string
  /** Rule title */
  title: string
  /** Rule description */
  description: string
  /** Step-by-step instructions for the assignee */
  instructions: string
  /** Cron expression (marks due date) */
  schedule: string
  /** Human-readable schedule description */
  schedule_description: string
  /** Role responsible for this task */
  assignee_role: AssigneeRole
  /** Specific assignee email */
  assignee_email?: PeriodicRuleCreateAssigneeEmail
  /** Severity level */
  severity: PolicyRuleSeverity
  /** Source policy document name */
  source_policy: string
  /**
   * Hours before due date to create task
   * @minimum 1
   */
  create_before?: number
  /**
   * Hours before due date when task is 'at risk'
   * @minimum 1
   */
  at_risk_before?: number
  /** Role to escalate to */
  escalation_role?: PeriodicRuleCreateEscalationRole
  /** Specific escalation email */
  escalation_email?: PeriodicRuleCreateEscalationEmail
  /** Excerpt from source policy document */
  source_snippet?: PeriodicRuleCreateSourceSnippet
}

/**
 * Rule category
 */
export type PeriodicRuleUpdateCategory = string | null

/**
 * Rule title
 */
export type PeriodicRuleUpdateTitle = string | null

/**
 * Rule description
 */
export type PeriodicRuleUpdateDescription = string | null

/**
 * Instructions
 */
export type PeriodicRuleUpdateInstructions = string | null

/**
 * Cron expression (marks due date)
 */
export type PeriodicRuleUpdateSchedule = string | null

/**
 * Schedule description
 */
export type PeriodicRuleUpdateScheduleDescription = string | null

/**
 * Assignee role
 */
export type PeriodicRuleUpdateAssigneeRole = AssigneeRole | null

/**
 * Assignee email
 */
export type PeriodicRuleUpdateAssigneeEmail = string | null

/**
 * Severity level
 */
export type PeriodicRuleUpdateSeverity = PolicyRuleSeverity | null

/**
 * Source policy document name
 */
export type PeriodicRuleUpdateSourcePolicy = string | null

/**
 * Hours before due date to create task
 */
export type PeriodicRuleUpdateCreateBefore = number | null

/**
 * Hours before due date when 'at risk'
 */
export type PeriodicRuleUpdateAtRiskBefore = number | null

/**
 * Role to escalate to
 */
export type PeriodicRuleUpdateEscalationRole = AssigneeRole | null

/**
 * Specific escalation email
 */
export type PeriodicRuleUpdateEscalationEmail = string | null

/**
 * Excerpt from source policy document
 */
export type PeriodicRuleUpdateSourceSnippet = string | null

/**
 * Request to update a periodic rule (all fields optional)
 */
export interface PeriodicRuleUpdate {
  /** Rule category */
  category?: PeriodicRuleUpdateCategory
  /** Rule title */
  title?: PeriodicRuleUpdateTitle
  /** Rule description */
  description?: PeriodicRuleUpdateDescription
  /** Instructions */
  instructions?: PeriodicRuleUpdateInstructions
  /** Cron expression (marks due date) */
  schedule?: PeriodicRuleUpdateSchedule
  /** Schedule description */
  schedule_description?: PeriodicRuleUpdateScheduleDescription
  /** Assignee role */
  assignee_role?: PeriodicRuleUpdateAssigneeRole
  /** Assignee email */
  assignee_email?: PeriodicRuleUpdateAssigneeEmail
  /** Severity level */
  severity?: PeriodicRuleUpdateSeverity
  /** Source policy document name */
  source_policy?: PeriodicRuleUpdateSourcePolicy
  /** Hours before due date to create task */
  create_before?: PeriodicRuleUpdateCreateBefore
  /** Hours before due date when 'at risk' */
  at_risk_before?: PeriodicRuleUpdateAtRiskBefore
  /** Role to escalate to */
  escalation_role?: PeriodicRuleUpdateEscalationRole
  /** Specific escalation email */
  escalation_email?: PeriodicRuleUpdateEscalationEmail
  /** Excerpt from source policy document */
  source_snippet?: PeriodicRuleUpdateSourceSnippet
}

/**
 * Response for list of periodic rules
 */
export interface PeriodicRulesListResponse {
  /** Total number of rules */
  total: number
  /** List of rules */
  rules: PolicyPeriodicRule[]
}

export type PeriodicTaskCompletedAt = string | null

export type PeriodicTaskError = string | null

export type PeriodicTaskAssigneeEmail = string | null

export type PeriodicTaskEscalationRole = string | null

export type PeriodicTaskEscalationEmail = string | null

export type PeriodicTaskResult = PeriodicTaskCompletionResult | null

/**
 * Schedule-based task for manual human execution.

Created by PeriodicAgent when a PolicyPeriodicRule's schedule triggers.
Humans execute the task and confirm completion.
 */
export interface PeriodicTask {
  id: string
  agent_name: Agent
  execution_status?: TaskExecutionStatus
  created_at: string
  completed_at: PeriodicTaskCompletedAt
  error: PeriodicTaskError
  rule_id: string
  rule_title: string
  rule_category: string
  description: string
  instructions: string
  assignee_role: string
  assignee_email: PeriodicTaskAssigneeEmail
  scheduled_date: string
  at_risk_date: string
  escalation_date: string
  escalation_role: PeriodicTaskEscalationRole
  escalation_email: PeriodicTaskEscalationEmail
  status: PeriodicTaskStatus
  result: PeriodicTaskResult
  notification_sent: boolean
  reminder_sent: boolean
  source_policy: string
  severity: PolicyRuleSeverity
}

/**
 * Response for batch task operations
 */
export interface PeriodicTaskBatchOperationResponse {
  /** Overall operation result message */
  message: string
  /** Total number of tasks in the batch */
  total: number
  /** Number of successful operations */
  successful: number
  /** Number of failed operations */
  failed: number
  /** Individual results for each task */
  results: BatchTaskResultPeriodicTask[]
}

/**
 * Request to start multiple tasks (approved_by comes from X-User-Name header)
 */
export interface PeriodicTaskBatchStartRequest {
  task_ids: string[]
}

export interface PeriodicTaskCompletedAuditEvent {
  event_type: 'periodic_task_completed_event'
  timestamp: string
  agent: string
  status: EventStatus
  task_id: string
  completed_by: string
  total_tasks: number
  successful: number
}

export type PeriodicTaskCompletionResultNotes = string | null

/**
 * Result when human completes a periodic task.
 */
export interface PeriodicTaskCompletionResult {
  completed_by: string
  completed_at: string
  notes?: PeriodicTaskCompletionResultNotes
}

export interface PeriodicTaskCreatedAuditEvent {
  event_type: 'periodic_task_created_event'
  timestamp: string
  agent: string
  status: EventStatus
  task_id: string
  rule_id: string
}

export interface PeriodicTaskStartedAuditEvent {
  event_type: 'periodic_task_started_event'
  timestamp: string
  agent: string
  status: EventStatus
  task_id: string
  total_tasks: number
  successful: number
}

/**
 * Status of periodic task completion.
 */
export type PeriodicTaskStatus =
  (typeof PeriodicTaskStatus)[keyof typeof PeriodicTaskStatus]

// eslint-disable-next-line @typescript-eslint/no-redeclare
export const PeriodicTaskStatus = {
  Pending: 'pending',
  InProgress: 'in_progress',
  Completed: 'completed',
  Overdue: 'overdue',
} as const

export interface PeriodicTasksListResponse {
  total: number
  tasks: PeriodicTask[]
}

export type PolicyConditionValue =
  | boolean
  | number
  | number
  | string
  | string[]
  | null

/**
 * Single condition to evaluate against data.
 */
export interface PolicyCondition {
  source: SourceSystem
  field: string
  operator: PolicyConditionOperator
  value?: PolicyConditionValue
}

/**
 * Operators for condition evaluation.
 */
export type PolicyConditionOperator =
  (typeof PolicyConditionOperator)[keyof typeof PolicyConditionOperator]

// eslint-disable-next-line @typescript-eslint/no-redeclare
export const PolicyConditionOperator = {
  Equals: 'equals',
  NotEquals: 'not_equals',
  LessThan: 'less_than',
  GreaterThan: 'greater_than',
  LessThanOrEqual: 'less_than_or_equal',
  GreaterThanOrEqual: 'greater_than_or_equal',
  Contains: 'contains',
  NotContains: 'not_contains',
  InList: 'in_list',
  IsNull: 'is_null',
  IsNotNull: 'is_not_null',
} as const

/**
 * Audit event for policy evaluation completion
 */
export interface PolicyEvaluationCompletedAuditEvent {
  event_type: 'policy_evaluation_completed_event'
  timestamp: string
  agent: string
  status: EventStatus
  violations_found: number
}

/**
 * Audit event for policy evaluation request
 */
export interface PolicyEvaluationRequestedAuditEvent {
  event_type: 'policy_evaluation_requested_event'
  timestamp: string
  agent: string
  status: EventStatus
  employee_count: number
}

/**
 * Assignee email
 */
export type PolicyExecutableRuleAssigneeEmail = string | null

/**
 * Role to escalate to
 */
export type PolicyExecutableRuleEscalationRole = AssigneeRole | null

/**
 * Specific escalation email
 */
export type PolicyExecutableRuleEscalationEmail = string | null

/**
 * Excerpt from source policy document
 */
export type PolicyExecutableRuleSourceSnippet = string | null

/**
 * Condition-based rule that triggers automated remediation tasks.

Evaluated when employee data changes (e.g., HR upload).
When conditions match, creates RemediationTasks for agent execution.
Requires approval before automated execution.
 */
export interface PolicyExecutableRule {
  /** Rule ID */
  rule_id: string
  /** Rule category */
  category: string
  /** Rule title */
  title: string
  /** Rule description */
  description: string
  /** Conditions that must all be true */
  conditions: PolicyCondition[]
  /** Severity level */
  severity: PolicyRuleSeverity
  /** Tasks to execute */
  tasks: PolicyTaskSpec[]
  /** Source policy document name */
  source_policy: string
  /** Assignee role */
  assignee_role: AssigneeRole
  /** Assignee email */
  assignee_email?: PolicyExecutableRuleAssigneeEmail
  /** Hours until task escalates */
  escalate_after?: number
  /** Role to escalate to */
  escalation_role?: PolicyExecutableRuleEscalationRole
  /** Specific escalation email */
  escalation_email?: PolicyExecutableRuleEscalationEmail
  /** Excerpt from source policy document */
  source_snippet?: PolicyExecutableRuleSourceSnippet
}

/**
 * Assignee email
 */
export type PolicyPeriodicRuleAssigneeEmail = string | null

/**
 * Role to escalate to
 */
export type PolicyPeriodicRuleEscalationRole = AssigneeRole | null

/**
 * Specific escalation email
 */
export type PolicyPeriodicRuleEscalationEmail = string | null

/**
 * Excerpt from source policy document
 */
export type PolicyPeriodicRuleSourceSnippet = string | null

/**
 * Schedule-based rule that triggers manual tasks for human execution.

Schedule marks the DUE DATE. Tasks are created create_before hours early
to allow time for completion. Tasks become "at risk" at_risk_before hours
before the due date.
 */
export interface PolicyPeriodicRule {
  /** Rule ID */
  rule_id: string
  /** Rule category */
  category: string
  /** Rule title */
  title: string
  /** Rule description */
  description: string
  /** Step-by-step instructions for the assignee */
  instructions: string
  /** Cron expression (marks due date) */
  schedule: string
  /** Human-readable schedule description */
  schedule_description: string
  /** Severity level */
  severity: PolicyRuleSeverity
  /** Whether this requires physical presence */
  is_physical?: boolean
  /** Source policy document name */
  source_policy: string
  /** Assignee role */
  assignee_role: AssigneeRole
  /** Assignee email */
  assignee_email?: PolicyPeriodicRuleAssigneeEmail
  /** Hours before due date to create task */
  create_before?: number
  /** Hours before due date when 'at risk' */
  at_risk_before?: number
  /** Role to escalate to */
  escalation_role?: PolicyPeriodicRuleEscalationRole
  /** Specific escalation email */
  escalation_email?: PolicyPeriodicRuleEscalationEmail
  /** Excerpt from source policy document */
  source_snippet?: PolicyPeriodicRuleSourceSnippet
}

export interface PolicyRuleCreatedAuditEvent {
  event_type: 'policy_rule_created_event'
  timestamp: string
  agent: string
  status: EventStatus
  rule_id: string
  rule_type: string
}

export interface PolicyRuleDeletedAuditEvent {
  event_type: 'policy_rule_deleted_event'
  timestamp: string
  agent: string
  status: EventStatus
  rule_id: string
  rule_type: string
}

/**
 * Severity level for rules.
 */
export type PolicyRuleSeverity =
  (typeof PolicyRuleSeverity)[keyof typeof PolicyRuleSeverity]

// eslint-disable-next-line @typescript-eslint/no-redeclare
export const PolicyRuleSeverity = {
  Critical: 'critical',
  High: 'high',
  Medium: 'medium',
  Low: 'low',
  Info: 'info',
} as const

export interface PolicyRuleUpdatedAuditEvent {
  event_type: 'policy_rule_updated_event'
  timestamp: string
  agent: string
  status: EventStatus
  rule_id: string
  rule_type: string
}

export interface PolicyRulesSeededAuditEvent {
  event_type: 'policy_rules_seeded_event'
  timestamp: string
  agent: string
  status: EventStatus
  executable_count: number
  periodic_count: number
}

export interface PolicyRulesSyncedAuditEvent {
  event_type: 'policy_rules_synced_event'
  timestamp: string
  agent: string
  status: EventStatus
  executable_count: number
  periodic_count: number
}

/**
 * Task specification in policy rules.
 */
export interface PolicyTaskSpec {
  agent: Agent
  task_type: RemediationTaskType
  description_template: string
  approval_required?: boolean
  priority?: number
}

export type QueryExecutedAuditEventError = string | null

/**
 * Audit event for query execution
 */
export interface QueryExecutedAuditEvent {
  event_type: 'query_executed_event'
  timestamp: string
  agent: string
  status: EventStatus
  query_id: string
  query_type: QueryTaskType
  error: QueryExecutedAuditEventError
}

/**
 * Supported query types for read-only agents.
 */
export type QueryTaskType = (typeof QueryTaskType)[keyof typeof QueryTaskType]

// eslint-disable-next-line @typescript-eslint/no-redeclare
export const QueryTaskType = {
  GetUser: 'get_user',
  SendNotification: 'send_notification',
} as const

export type RemediationTaskCompletedAt = string | null

export type RemediationTaskError = string | null

export type RemediationTaskRemediatedAt = string | null

export type RemediationTaskRemediatedBy = string | null

export type RemediationTaskResult =
  | RemediationTaskDisableAccountResult
  | RemediationTaskRemoveFromGroupsResult
  | RemediationTaskCreateTicketResult
  | null

export type RemediationTaskTargetUserEmail = string | null

export type RemediationTaskRuleId = string | null

export type RemediationTaskRuleCategory = string | null

export type RemediationTaskRuleSeverity = PolicyRuleSeverity | null

export type RemediationTaskSourcePolicy = string | null

export type RemediationTaskSourceType = TaskSourceType | null

export type RemediationTaskAssigneeRole = string | null

export type RemediationTaskAssigneeEmail = string | null

export type RemediationTaskEscalationRole = string | null

export type RemediationTaskEscalationEmail = string | null

/**
 * Task to be taken by an agent
 */
export interface RemediationTask {
  id: string
  agent_name: Agent
  execution_status?: TaskExecutionStatus
  created_at: string
  completed_at: RemediationTaskCompletedAt
  error: RemediationTaskError
  task_type: RemediationTaskType
  description: string
  approval_status: RemediationTaskApprovalStatus
  approval_required: boolean
  remediated_at: RemediationTaskRemediatedAt
  remediated_by: RemediationTaskRemediatedBy
  escalation_date: string
  result: RemediationTaskResult
  target_user_email: RemediationTaskTargetUserEmail
  rule_id: RemediationTaskRuleId
  rule_category: RemediationTaskRuleCategory
  rule_severity: RemediationTaskRuleSeverity
  source_policy: RemediationTaskSourcePolicy
  source_type: RemediationTaskSourceType
  assignee_role: RemediationTaskAssigneeRole
  assignee_email: RemediationTaskAssigneeEmail
  escalation_role: RemediationTaskEscalationRole
  escalation_email: RemediationTaskEscalationEmail
}

/**
 * Status of task approval (HITL).
 */
export type RemediationTaskApprovalStatus =
  (typeof RemediationTaskApprovalStatus)[keyof typeof RemediationTaskApprovalStatus]

// eslint-disable-next-line @typescript-eslint/no-redeclare
export const RemediationTaskApprovalStatus = {
  PendingApproval: 'pending_approval',
  Approved: 'approved',
  Rejected: 'rejected',
  AutoApproved: 'auto_approved',
} as const

/**
 * Response for batch task operations
 */
export interface RemediationTaskBatchOperationResponse {
  /** Overall operation result message */
  message: string
  /** Total number of tasks in the batch */
  total: number
  /** Number of successful operations */
  successful: number
  /** Number of failed operations */
  failed: number
  /** Individual results for each task */
  results: BatchTaskResultRemediationTask[]
}

export type RemediationTaskCreateTicketResultTicketUrl = string | null

export interface RemediationTaskCreateTicketResult {
  ticket_number: string
  ticket_id: string
  target_user_email: string
  created_at: string
  ticket_url?: RemediationTaskCreateTicketResultTicketUrl
  success?: boolean
}

/**
 * Response for Remediation Tasks Statistics
 */
export interface RemediationTaskDataPoint {
  date: string
  created: number
  completed: number
  open: number
  failed: number
  escalated: number
}

export interface RemediationTaskDisableAccountResult {
  user_id: string
  user_email: string
  user_name: string
  disabled_at: string
  success?: boolean
}

export interface RemediationTaskRemoveFromGroupsResult {
  user_id: string
  user_email: string
  user_name: string
  total_groups: number
  removed: number
  failed: number
  groups: string[]
  success?: boolean
}

/**
 * Supported task types with target system.
 */
export type RemediationTaskType =
  (typeof RemediationTaskType)[keyof typeof RemediationTaskType]

// eslint-disable-next-line @typescript-eslint/no-redeclare
export const RemediationTaskType = {
  EntraDisableAccount: 'entra_disable_account',
  EntraRemoveGroups: 'entra_remove_groups',
  EntraAssignGroups: 'entra_assign_groups',
  EntraUpdateGroups: 'entra_update_groups',
  EntraCreateAccount: 'entra_create_account',
  SapDisableAccount: 'sap_disable_account',
} as const

/**
 * Response for root endpoint
 */
export interface RootResponse {
  /** Service name */
  service: string
  /** API version */
  version: string
  /** Service status */
  status: string
  /** Documentation URL path */
  docs: string
}

/**
 * Response for seeding rules from JSON defaults
 */
export interface RulesSeedResponse {
  /** Operation status */
  status: string
  /** Number of executable rules imported */
  executable_count: number
  /** Number of periodic rules imported */
  periodic_count: number
  /** Detailed message */
  message: string
}

/**
 * Response for syncing rules from SharePoint
 */
export interface RulesSyncResponse {
  /** Operation status */
  status: string
  /** Number of executable rules synced */
  executable_count: number
  /** Number of periodic rules synced */
  periodic_count: number
  /** Detailed message */
  message: string
}

export type SFExportResponseError = string | null

/**
 * SuccessFactors export record
 */
export interface SFExportResponse {
  id: string
  created_at: string
  filename: string
  status: string
  amount_records: number
  error?: SFExportResponseError
}

/**
 * Response for list of SF exports
 */
export interface SFExportsListResponse {
  total: number
  exports: SFExportResponse[]
}

/**
 * Sort order for audit log queries.
 */
export type SortOrder = (typeof SortOrder)[keyof typeof SortOrder]

// eslint-disable-next-line @typescript-eslint/no-redeclare
export const SortOrder = {
  Asc: 'asc',
  Desc: 'desc',
} as const

/**
 * Data source systems for conditions.
 */
export type SourceSystem = (typeof SourceSystem)[keyof typeof SourceSystem]

// eslint-disable-next-line @typescript-eslint/no-redeclare
export const SourceSystem = {
  SapSf: 'sap_sf',
  SapEcc: 'sap_ecc',
  Entra: 'entra',
} as const

/**
 * Response for compliancy statistics
 */
export interface StatisticsCompliancyResponse {
  compliancy: StatisticsCompliancyValue
  open_remediation_tasks: number
  escalated_remediation_tasks: number
  open_periodic_tasks: number
  at_risk_periodic_tasks: number
  escalated_periodic_tasks: number
}

export type StatisticsCompliancyValue =
  (typeof StatisticsCompliancyValue)[keyof typeof StatisticsCompliancyValue]

// eslint-disable-next-line @typescript-eslint/no-redeclare
export const StatisticsCompliancyValue = {
  Compliant: 'compliant',
  AtRisk: 'at_risk',
  Incompliant: 'incompliant',
} as const

export interface StatisticsTaskTotals {
  total_processed: number
  total_successful: number
  total_failed: number
}

/**
 * Audit event for task approval
 */
export interface TaskApprovedAuditEvent {
  event_type: 'task_approved_event'
  timestamp: string
  agent: string
  status: EventStatus
  task_id: string
  approved_by: string
}

/**
 * Audit event for task creation
 */
export interface TaskCreatedAuditEvent {
  event_type: 'task_created_event'
  timestamp: string
  agent: string
  status: EventStatus
  task_id: string
  task_type: RemediationTaskType
}

export type TaskExecutedAuditEventResultAnyOf = { [key: string]: unknown }

export type TaskExecutedAuditEventResult =
  TaskExecutedAuditEventResultAnyOf | null

export type TaskExecutedAuditEventError = string | null

/**
 * Audit event for remediation task execution
 */
export interface TaskExecutedAuditEvent {
  event_type: 'task_executed_event'
  timestamp: string
  agent: string
  status: EventStatus
  task_id: string
  task_type: RemediationTaskType
  target_user_email: string
  execution_status: TaskExecutionStatus
  approval_status: RemediationTaskApprovalStatus
  result: TaskExecutedAuditEventResult
  error: TaskExecutedAuditEventError
}

/**
 * Status of task execution.
 */
export type TaskExecutionStatus =
  (typeof TaskExecutionStatus)[keyof typeof TaskExecutionStatus]

// eslint-disable-next-line @typescript-eslint/no-redeclare
export const TaskExecutionStatus = {
  Pending: 'pending',
  InProgress: 'in_progress',
  Completed: 'completed',
  Failed: 'failed',
} as const

export type TaskRejectedAuditEventReason = string | null

/**
 * Audit event for task rejection
 */
export interface TaskRejectedAuditEvent {
  event_type: 'task_rejected_event'
  timestamp: string
  agent: string
  status: EventStatus
  task_id: string
  rejected_by: string
  reason: TaskRejectedAuditEventReason
}

/**
 * Origin of data that triggered evaluation.
 */
export type TaskSourceType =
  (typeof TaskSourceType)[keyof typeof TaskSourceType]

// eslint-disable-next-line @typescript-eslint/no-redeclare
export const TaskSourceType = {
  SfUpload: 'sf_upload',
  SfPoll: 'sf_poll',
  Manual: 'manual',
} as const

/**
 * Response for list of tasks
 */
export interface TasksListResponse {
  /** Total number of tasks */
  total: number
  /** List of tasks */
  tasks: RemediationTask[]
}

/**
 * Response for triggering a periodic rule manually
 */
export interface TriggerRuleResponse {
  success: boolean
  message: string
}

export type ValidationErrorLocItem = string | number

export interface ValidationError {
  loc: ValidationErrorLocItem[]
  msg: string
  type: string
}

export type HrListExportsParams = {
  /**
   * @maximum 100
   */
  limit?: number
}

export type PolicyDeleteExecutableRule200 = { [key: string]: string }

export type PolicyDeletePeriodicRule200 = { [key: string]: string }

export type TasksGetPeriodicTasksParams = {
  /**
   * Filter by task status
   */
  status?: PeriodicTaskStatus | null
  /**
   * Filter by assignee email
   */
  assignee_email?: string | null
  /**
   * Filter by escalation status
   */
  escalated?: boolean | null
  /**
   * Max number of tasks to return
   */
  limit?: number | null
  /**
   * Number of tasks to skip
   */
  offset?: number | null
  /**
   * Sort order by created_at
   */
  order?: SortOrder
}

export type TasksGetAllTasksParams = {
  /**
   * Filter by execution status
   */
  execution_status?: TaskExecutionStatus | null
  /**
   * Filter by assignee email
   */
  assignee_email?: string | null
  /**
   * Filter by escalation status
   */
  escalated?: boolean | null
  /**
   * Filter by source type
   */
  source_type?: TaskSourceType | null
  /**
   * Max number of tasks to return
   */
  limit?: number | null
  /**
   * Number of tasks to skip
   */
  offset?: number | null
  /**
   * Sort order by created_at
   */
  order?: SortOrder
}

export type NotificationsGetNotificationsParams = {
  read?: boolean | null
  task_id?: string | null
}

export type AuditGetAuditLogsParams = {
  /**
   * Maximum number of entries to return
   * @minimum 1
   * @maximum 1000
   */
  limit?: number
  /**
   * Number of entries to skip for pagination
   * @minimum 0
   */
  skip?: number
  /**
   * Alias for skip
   * @minimum 0
   */
  offset?: number
  /**
   * Search across task_id, target_user, event_name
   */
  search?: string | null
  /**
   * Filter by agent name
   */
  agent?: AuditGetAuditLogsAgent
  /**
   * Filter by event status
   */
  status?: EventStatus | null
  /**
   * Sort order (asc/desc)
   */
  order?: SortOrder
}

export type AuditGetAuditLogsAgent =
  (typeof AuditGetAuditLogsAgent)[keyof typeof AuditGetAuditLogsAgent]

// eslint-disable-next-line @typescript-eslint/no-redeclare
export const AuditGetAuditLogsAgent = {
  RiaAgent: 'ria_agent',
  HrAgent: 'hr_agent',
  EntraAgent: 'entra_agent',
  PolicyAgent: 'policy_agent',
  TopdeskAgent: 'topdesk_agent',
  PeriodicAgent: 'periodic_agent',
} as const

export type StatisticsGetRemediationTasksStatisticsParams = {
  /**
   * Start date
   */
  start_date: string
  /**
   * End date
   */
  end_date: string
}
